// Selector rápido de especificación tipo "chips":
//   [Interfoleado] [Churrasco] [Entero] [ + ]
//
// - 1 click sobre un chip = lo asigna a la fila.
// - 1 click sobre el chip ya seleccionado = lo desasigna (vuelve a "sin
//   especificación").
// - El botón "+" abre un mini inline-input para crear una especificación
//   nueva sobre la marcha. Se guarda vía API, se refresca la lista y
//   automáticamente queda asignada a la fila que la creó.
//
// Diseño: chips compactos con estado visual claro (seleccionado = ámbar
// sólido, no seleccionado = neutro pero con hover). Cuando hay muchas
// especificaciones el contenedor hace wrap.
import { useEffect, useMemo, useRef, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";

// Cache global compartida entre todas las instancias del combobox para
// evitar hacer N pedidos idénticos cuando hay varios ítems en un pedido.
const _cache = { items: null, promise: null, listeners: new Set() };

async function _fetchList() {
  if (_cache.items) return _cache.items;
  if (_cache.promise) return _cache.promise;
  _cache.promise = api.get("/especificaciones").then(({ data }) => {
    _cache.items = data || [];
    _cache.promise = null;
    _cache.listeners.forEach((cb) => cb(_cache.items));
    return _cache.items;
  }).catch(() => {
    _cache.items = [];
    _cache.promise = null;
    return [];
  });
  return _cache.promise;
}

function _invalidate() {
  _cache.items = null;
  _cache.promise = null;
}

export default function EspecificacionCombobox({
  value = "",
  onChange,
  testId = "espec-combo",
}) {
  const [items, setItems] = useState(_cache.items || []);
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [nuevo, setNuevo] = useState("");
  const nuevoRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    _fetchList().then((data) => { if (mounted) setItems(data); });
    const cb = (data) => { if (mounted) setItems(data); };
    _cache.listeners.add(cb);
    return () => { mounted = false; _cache.listeners.delete(cb); };
  }, []);

  useEffect(() => {
    if (showCreate) {
      requestAnimationFrame(() => nuevoRef.current?.focus());
    }
  }, [showCreate]);

  const toggle = (nombre) => {
    onChange?.(value === nombre ? "" : nombre);
  };

  const handleCreate = async () => {
    const nombre = (nuevo || "").trim();
    if (!nombre) { setShowCreate(false); return; }
    setCreating(true);
    try {
      const { data } = await api.post("/especificaciones", { nombre });
      toast.success(`"${data.nombre}" creada`);
      _invalidate();
      const fresh = await _fetchList();
      setItems(fresh);
      onChange?.(data.nombre); // asignar automáticamente al ítem que la creó
      setNuevo("");
      setShowCreate(false);
    } catch (err) {
      // 409 = ya existe → asignarla tal cual
      const detail = err?.response?.data?.detail || "";
      if (err?.response?.status === 409) {
        const existente = items.find((e) => (e.nombre || "").toLowerCase() === nombre.toLowerCase());
        if (existente) onChange?.(existente.nombre);
        toast.info(detail || "Ya existe, la asigné a este ítem");
        setNuevo("");
        setShowCreate(false);
      } else {
        toast.error(detail || "No se pudo crear");
      }
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="flex items-center gap-1" data-testid={testId}>
      {showCreate ? (
        <div className="inline-flex items-center gap-1 border border-emerald-300 bg-emerald-50 rounded px-2 py-1 flex-1">
          <input
            ref={nuevoRef}
            value={nuevo}
            onChange={(ev) => setNuevo(ev.target.value)}
            onKeyDown={(ev) => {
              if (ev.key === "Enter") { ev.preventDefault(); handleCreate(); }
              if (ev.key === "Escape") { setShowCreate(false); setNuevo(""); }
            }}
            onBlur={() => { if (!creating && !nuevo.trim()) setShowCreate(false); }}
            placeholder="Nueva especificación…"
            disabled={creating}
            className="text-xs bg-transparent border-0 outline-none flex-1 text-emerald-900 placeholder:text-emerald-500 disabled:opacity-50"
            data-testid={`${testId}-new-input`}
          />
          <button
            type="button"
            onClick={handleCreate}
            disabled={creating || !nuevo.trim()}
            className="text-[10px] font-bold text-emerald-700 disabled:opacity-30 px-1"
            data-testid={`${testId}-new-confirm`}
          >
            ↵
          </button>
          <button
            type="button"
            onClick={() => { setShowCreate(false); setNuevo(""); }}
            className="text-[10px] text-neutral-500 hover:text-red-600 px-1"
          >
            ✕
          </button>
        </div>
      ) : (
        <>
          <select
            value={value || ""}
            onChange={(e) => {
              const v = e.target.value;
              if (v === "__nueva__") { setShowCreate(true); return; }
              onChange?.(v);
            }}
            className="flex-1 h-8 text-xs border border-neutral-300 rounded px-2 py-0 bg-white font-sans focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
            data-testid={`${testId}-select`}
          >
            <option value="">— sin especificación —</option>
            {items.map((e) => (
              <option key={e.id} value={e.nombre}>{e.nombre}</option>
            ))}
            <option value="__nueva__">+ Crear nueva…</option>
          </select>
        </>
      )}
    </div>
  );
}
